/**
 * Hook for async trip summary generation
 * Handles long trips that would timeout with synchronous generation
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { apiClient } from '../../../utils/apiClient';

export interface SummaryJobStatus {
  jobId: string;
  userId: string;
  tripId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  result?: {
    summary: any;
    placePhotos: Record<string, string>;
    generatedAt: any;
    optimizeOrder: boolean;
    cached: boolean;
  };
  error?: string;
  createdAt?: string;
  startedAt?: string;
  completedAt?: string;
}

interface UseAsyncSummaryGenerationOptions {
  onComplete?: (result: SummaryJobStatus['result']) => void;
  onError?: (error: string) => void;
  pollInterval?: number; // milliseconds (default: 3000)
}

export const useAsyncSummaryGeneration = (options?: UseAsyncSummaryGenerationOptions) => {
  const {
    onComplete,
    onError,
    pollInterval = 3000 // Poll every 3 seconds
  } = options || {};

  const [isGenerating, setIsGenerating] = useState(false);
  const [jobStatus, setJobStatus] = useState<SummaryJobStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Poll job status once
   */
  const pollJobStatus = useCallback(async (jobId: string) => {
    try {
      const response = await apiClient.get(`/api/advanced-trip/summary-job/${jobId}`);

      if (!response.ok) {
        throw new Error('Failed to get job status');
      }

      const status: SummaryJobStatus = await response.json();

      setJobStatus(status);

      // If job is completed or failed, stop polling
      if (status.status === 'completed' || status.status === 'failed') {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setIsGenerating(false);

        if (status.status === 'completed' && status.result) {
          if (onComplete) {
            onComplete(status.result);
          }
        } else if (status.status === 'failed') {
          console.error('[AsyncSummary] ❌ Generation failed:', status.error);
          const errorMessage = status.error || 'Summary generation failed';
          setError(errorMessage);
          if (onError) {
            onError(errorMessage);
          }
        }
      }
    } catch (error: any) {
      console.error('[AsyncSummary] Error polling status:', error);
      // Don't stop polling on error - server might be temporarily unavailable
      // Will retry on next interval
    }
  }, [onComplete, onError]);

  /**
   * Start polling for job status
   */
  const startPolling = useCallback((jobId: string) => {
    // Clear any existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    // Poll immediately
    pollJobStatus(jobId);

    // Then poll at regular intervals
    pollIntervalRef.current = setInterval(() => {
      pollJobStatus(jobId);
    }, pollInterval);
  }, [pollInterval, pollJobStatus]);

  /**
   * Start async summary generation
   */
  const startGeneration = useCallback(async (
    userId: string,
    tripId: string,
    optimizeOrder?: boolean
  ) => {
    try {
      setIsGenerating(true);
      setError(null);
      setJobStatus(null);

      // Use apiClient which automatically adds Firebase auth token
      const response = await apiClient.post('/api/advanced-trip/generate-summary-async', {
        userId,
        tripId,
        optimizeOrder
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Return response for caller to handle (e.g., 429 rate limits)
        setIsGenerating(false);
        return {
          success: false,
          status: response.status,
          error: errorData
        };
      }

      const data = await response.json();

      // Set initial job status
      setJobStatus({
        jobId: data.jobId,
        userId,
        tripId,
        status: 'pending',
        progress: 0
      });

      // Start polling
      startPolling(data.jobId);

      return {
        success: true,
        jobId: data.jobId
      };
    } catch (error: any) {
      console.error('[AsyncSummary] Error starting generation:', error);
      const errorMessage = error.message || 'Failed to start summary generation';
      setError(errorMessage);
      setIsGenerating(false);
      
      if (onError) {
        onError(errorMessage);
      }
      
      return {
        success: false,
        status: 500,
        error: { error: errorMessage }
      };
    }
  }, [onError, startPolling]);

  /**
   * Stop polling
   */
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  /**
   * Cancel generation
   */
  const cancelGeneration = useCallback(() => {
    // Stop polling
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    
    // Reset state
    setIsGenerating(false);
    setJobStatus(null);
    setError(null);
  }, []);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    // State
    isGenerating,
    jobStatus,
    error,
    progress: jobStatus?.progress || 0,
    
    // Actions
    startGeneration,
    cancelGeneration,
    
    // Helpers
    isCompleted: jobStatus?.status === 'completed',
    isFailed: jobStatus?.status === 'failed',
    isPending: jobStatus?.status === 'pending',
    isProcessing: jobStatus?.status === 'processing',
  };
};

